import React from "react";
import { useSelector } from "@xstate/react";
import { useSignUpMachine } from "../context/SignUpMachineContext";
import { SignUpStep } from "../constants/signup-step.enum";

// Define the order of steps for progress calculation
const STEP_ORDER = {
  // Control Flow
  [SignUpStep.Company]: 1,
  [SignUpStep.Country]: 2,
  [SignUpStep.YearsOpen]: 3,
  [SignUpStep.Revenue]: 4,
  [SignUpStep.Vertical]: 5,
  [SignUpStep.BusinessNeeds]: 6,
  [SignUpStep.CurrentSoftware]: 7,
  [SignUpStep.ContactInfo]: 8,
  [SignUpStep.Free]: 9,
  [SignUpStep.AccountConfirmation]: 10,
  [SignUpStep.CreatePassword]: 11,

  // Variant Flow (with Email first)
  [SignUpStep.Email]: 1,
};

// Define the total number of steps for each flow
const TOTAL_STEPS = {
  control: 11, // Company through CreatePassword
  variant: 11, // Email through CreatePassword
};

// Steps where we don't want to show the progress bar
const HIDDEN_PROGRESS_STEPS = [SignUpStep.Unsupported, SignUpStep.Final];

// Enhanced psychological progress calculation
// - First step starts at 35%
// - Front-loaded progress to provide more encouragement early
// - Non-linear progression that accelerates toward the end
const calculatePsychologicalProgress = (
  currentStep: number,
  totalSteps: number,
): number => {
  if (currentStep <= 0) return 0;
  if (currentStep >= totalSteps) return 100;

  // Base progress with a front-loaded curve
  // First step already at 35%
  if (currentStep === 1) return 35;

  // For the remaining steps (2 to totalSteps)
  // We'll distribute the remaining 65% with a slightly curved acceleration

  // Calculate what portion of the journey we've completed (0 to 1 scale, not counting first step)
  const completedPortion = (currentStep - 1) / (totalSteps - 1);

  // Apply a slight curve to the progress (power of 0.85 makes it slightly front-loaded)
  // This gives more progress for early steps and slows down towards the end
  const curvedProgress = Math.pow(completedPortion, 0.85);

  // Scale the curved progress to the remaining 65%
  const scaledProgress = 35 + curvedProgress * 65;

  return Math.min(Math.round(scaledProgress), 100);
};

interface ProgressBarProps {
  className?: string;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ className = "" }) => {
  const service = useSignUpMachine();
  const currentState = useSelector(service, (state) => state.value);
  const machineId = useSelector(service, (state) => state.machine.id);

  // Don't show progress bar on certain steps
  if (HIDDEN_PROGRESS_STEPS.includes(currentState as SignUpStep)) {
    return null;
  }

  // Determine if we're using control or variant flow
  const isVariant = machineId === "signup-variant";

  // Get current step position
  const currentStepPosition = STEP_ORDER[currentState as SignUpStep] || 1;

  // Get total steps for this flow
  const totalSteps = isVariant ? TOTAL_STEPS.variant : TOTAL_STEPS.control;

  // Calculate psychological progress percentage
  const progressPercentage = calculatePsychologicalProgress(
    currentStepPosition,
    totalSteps,
  );

  return (
    <div className={`tw-w-full tw-opacity-90 tw-px-4 ${className}`}>
      <div className="tw-h-2 tw-bg-gray-200 tw-rounded-full tw-overflow-hidden">
        <div
          className="tw-h-full tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-rounded-full tw-transition-all tw-duration-700 tw-ease-out"
          style={{ width: `${progressPercentage}%` }}
        >
          <div className="tw-h-full tw-w-full tw-opacity-20 tw-bg-[radial-gradient(at_right_center,_white,_transparent)]"></div>
        </div>
      </div>
    </div>
  );
};

export default ProgressBar;
